import { getShouldQualifyOfferHotelCancellation } from 'selectors/hotelCancellationSelectors'
import { hasAirportTransferPackageInclusions } from 'lib/offer/airportTransferPackageGrouping'
import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import Image from 'components/Common/Image/Image'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import RenderInclusions from 'components/OfferPage/AlwaysOn/RenderInclusions'
import BaseBundleOptionItemTile from 'components/OfferPage/BundleAndSave/BundleAndSaveTiles/BaseBundleOptionItemTile'
import InclusionsSection from 'components/OfferPage/FlashHotel/InclusionsSection'
import AgentExclusivePackageLabel from 'components/Common/Labels/AgentExclusivePackageLabel'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import ModalContext from 'contexts/ModalContext'
import { takeOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { nonNullable } from 'lib/array/arrayUtils'
import moment from 'moment/moment'
import { useCallback, useContext, useMemo } from 'react'
import BundleAndSaveHotelTileInclusionsModalView from './BundleAndSaveHotelTileInclusionsModalView'
import { FormattedMessage, useIntl } from 'react-intl'

interface Props {
  showHotelOnly: boolean
  offer: App.HotelOffer
  pkg: App.HotelPackage
  className?: string
  selectedDuration: number
  checkIn?: string
  rooms: Array<App.RoomOccupants>
  availableForPurchase: boolean
}

function BundleAndSaveHotelTile(props: Props) {
  const intl = useIntl()
  const {
    showHotelOnly,
    offer,
    rooms,
    pkg,
    className,
    selectedDuration,
    checkIn,
    availableForPurchase,
  } = props
  const showModal = useContext(ModalContext)
  const showAgentExclusivePackageLabel = !!pkg.isAgentHubExclusive && !offer.isAgentHubExclusive
  const propertyLogoAlt = offer.property?.name
    ? intl.formatMessage({
        id: 'G_-Y7HppK3W5YEYqnO4m',
        defaultMessage: '{propertyName} logo',
        description: 'Alt text for a property logo shown in a bundle and save hotel tile when the property name is known',
      }, { propertyName: offer.property.name })
    : intl.formatMessage({
        id: 'zsolU-y4Nhi0ROwfGuxA',
        defaultMessage: 'Vendor logo',
        description: 'Fallback alt text for a property logo shown in a bundle and save hotel tile',
      })

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkInMoment?.clone().add(pkg.duration, 'days'), [checkInMoment, pkg.duration])
  const { inclusions, bonusInclusions } = useOfferInclusions(pkg, { checkIn: checkInMoment, checkOut: checkOutMoment })
  const onInclusionReveal = useCallback(() => {
    showModal(<BundleAndSaveHotelTileInclusionsModalView
      offer={offer}
      pkg={pkg}
      inclusions={inclusions}
      bonusInclusions={bonusInclusions}
      amenities={pkg.roomType.amenities}
      roomPolicyDescription={pkg.roomPolicyDescription}
    />)
  }, [bonusInclusions, inclusions, offer, pkg, showModal])

  const locationLabel = useMemo(() => {
    return nonNullable([
      offer.locationHeading,
      offer.locationSubheading,
    ]).join(', ')
  }, [offer])

  return (
    <BaseBundleOptionItemTile className={className} room={pkg.roomType}>
      <Group direction="vertical" gap={12}>
        <Group direction="horizontal" horizontalAlign="space-between" gap={4}>
          <div>
            {!!locationLabel && <BodyText variant="small">{locationLabel}</BodyText>}
            <Heading variant="heading5">{offer.property?.name}</Heading>
          </div>

          {offer.property?.logoImageId && <CSSBreakpoint min="tablet">
            <Image
              dpr={2}
              enhancedFormats={false}
              id={offer.property.logoImageId}
              width={80}
              aspectRatio="2"
              alt={propertyLogoAlt}
            />
          </CSSBreakpoint>}
        </Group>

        <div>
          {showAgentExclusivePackageLabel && <AgentExclusivePackageLabel />}
          {!!pkg.roomType?.name && <BodyText variant="large" weight="bold" colour="light-foreground-alt">
            {pkg.roomType?.name}
          </BodyText>}
          {!!pkg.roomType?.sizeSqm && <BodyText variant="medium">
            <FormattedMessage
              id="xsI2-1MfmdXiQ0G7xTmw"
              defaultMessage="<bold>Size:</bold> {sizeSqm} m<sup>2</sup>"
              description="Room size label with square meter value on bundle and save hotel tile"
              values={{
                sizeSqm: pkg.roomType.sizeSqm,
                bold: (chunks) => <b>{chunks}</b>,
                sup: (chunks) => <sup>{chunks}</sup>,
              }}
            />
          </BodyText>}
          {availableForPurchase && offer.type === OFFER_TYPE_HOTEL && <FlashCancellationPolicies
            cancellationPolicy={pkg?.roomRate?.cancellationPolicy}
            checkInDate={checkIn}
            // Bundle+Save offer tiles are not on charter routes; pass explicitly so a leftover
            // checkout-cart departure is not applied.
            charterDepartDate={undefined}
            offerId={pkg.offerId}
            numberOfDateChangesAllowed={offer.numberOfDateChangesAllowed}
            showHotelOnly={showHotelOnly || hasAirportTransferPackageInclusions(pkg)}
          />}
          {availableForPurchase && offer.type === OFFER_TYPE_ALWAYS_ON && <TAOCancellationPolicies
            offerId={offer.id}
            useDynamicCancellationPolicies={offer.property?.useDynamicCancellationPolicies && !pkg.ignoreDynamicCancellationPolicy}
            timezoneOffset={offer.property?.timezoneOffset}
            uniqueKey={pkg.uniqueKey}
            checkInDate={checkIn}
            checkOutDate={checkIn ? moment(checkIn).add(selectedDuration, 'days').format(ISO_DATE_FORMAT) : undefined}
            occupants={rooms}
            cancellationPolicy={pkg?.roomRate?.cancellationPolicy}
            showHotelOnly={showHotelOnly || hasAirportTransferPackageInclusions(pkg)}
          />}
        </div>

        <Group direction="vertical" gap={4}>
          <LargeContentToggle hideText maxHeight={164}>
            {offer.type === OFFER_TYPE_ALWAYS_ON && (
              <RenderInclusions
                offer={offer}
                bonusInclusions={bonusInclusions}
                inclusions={inclusions}
              />
            )}
            {offer.type === OFFER_TYPE_HOTEL && (
              <InclusionsSection offer={offer} inclusions={inclusions} bonusInclusions={bonusInclusions} />
            )}
          </LargeContentToggle>
          {pkg.roomType &&
            <TextLink
              size="medium"
              weight="bold"
              onClick={onInclusionReveal}
            >
              {intl.formatMessage({
                id: 'D8sxXdwDKLFQZh8pIZSy',
                defaultMessage: 'View all inclusions & details',
                description: 'Link to view all inclusions and details for a bundle and save hotel tile',
              })}
            </TextLink>}
        </Group>
      </Group>
    </BaseBundleOptionItemTile>
  )
}

export default withOfferPageState((state, props: Props) => {
  const currentOfferId = props.offer.id
  const currentState = takeOfferState(state, currentOfferId)

  return {
    showHotelOnly: getShouldQualifyOfferHotelCancellation(state),
    selectedDuration: currentState.duration,
    rooms: currentState.rooms,
    checkIn: currentState.checkInDate,
  }
}, BundleAndSaveHotelTile)
